/* import __COLOCATED_TEMPLATE__ from './company-profile-teams-manager-item.hbs'; */
import Component from '@glimmer/component';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { showModal } from 'sh-www/utils/modal-controls';
import { htmlSafe } from '@ember/template';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-company-teams-manager-item';

const NEW_TEAM_NAME_FIRST = 'My First Team';
const NEW_TEAM_NAME_DEFAULT = 'New Team';

const TEAM_COLOR_DEFAULTS = [
  'A93BF3',
  '127ACA',
  '3C8729',
  'DB238B',
  '9B6F07',
  '016B73',
  'C75306',
  '5537CB',
  '8B1571',
  '18338F',
  'C31823',
];

class CompanyProfileTeamItem extends Component {
  @tracked editMode = false;
  @tracked showDeleteSuccess = false;
  @tracked teamName = '';
  @tracked teamDescription = '';
  @tracked teamColorHex = '';

  constructor() {
    super(...arguments);

    // if no team model is passed in then we are creating a new team
    if (!this.args.team) {
      this.editMode = true;
    }

    this.teamName = this.args.team?.name ?? this.newTeamName;
    this.teamDescription = this.args.team?.description ?? '';
    this.teamColorHex = this.args.team?.colorHex ?? this.newTeamColorHex;
  }

  get className() {
    return CLASS_NAME;
  }

  get classNames() {
    return clsx(
      this.args.class,
      this.className,
      this.editMode && `${this.className}--editing`,
    );
  }

  get isLoading() {
    return this.saveTask.isRunning
      || this.deleteTask.isRunning;
  }

  get teamIconStyles() {
    return htmlSafe(`--team-icon-color: #${this.teamColorHex};`);
  }

  get newTeamName() {
    const { allTeams } = this.args;

    // first team gets a unique name
    if (!allTeams.length) {
      return NEW_TEAM_NAME_FIRST;
    }

    // dont suggest duplicate names
    const defaultNameMatches = new RegExp(`^${NEW_TEAM_NAME_DEFAULT}(?: (\\([0-9]+\\)))?$`, 'g');
    const teamsWithDefaultName = allTeams.reduce((count, { name }) => {
      if (name.match(defaultNameMatches)) {
        count += 1;
      }

      return count;
    }, 0);

    return teamsWithDefaultName
      ? `${NEW_TEAM_NAME_DEFAULT} (${teamsWithDefaultName})`
      : NEW_TEAM_NAME_DEFAULT;
  }

  get newTeamColorHex() {
    const teamColors = TEAM_COLOR_DEFAULTS;
    const teamCount = this.args.allTeams.length;

    return teamColors[teamCount % teamColors.length];
  }

  get hideActions() {
    return !this.args.currentUserCanEdit;
  }

  @task
  *saveTask(companyId, name, description, colorHex, existingTeamId = null) {
    yield this.args.saveTeamTask.perform(companyId, name, description, colorHex, existingTeamId);
    const updatedCompanyTeams = yield this.args.loadCompanyTeamsTask.perform(companyId);
    this.args.updateCompanyTeams(updatedCompanyTeams);

    this.editMode = false;

    if (existingTeamId) {
      this.args.cancelEditTeam();
    } else {
      this.args.cancelAddTeam();
    }
  }

  @task
  *deleteTask(companyId, teamId) {
    yield this.args.deleteTeam.perform(teamId);

    const updatedCompanyTeams = yield this.args.loadCompanyTeamsTask.perform(companyId);

    // reload user relationships to teams. dont yield, will drop upon retry
    this.args.reloadCompanyMembers.perform(companyId);

    // show inline that the team has been deleted before it disappears
    this.showDeleteSuccess = true;
    yield timeout(2000);

    this.args.updateCompanyTeams(updatedCompanyTeams);
  }

  @action
  editTeam() {
    this.editMode = true;
    this.args.editTeam?.();
  }

  @action
  editTeamCancel() {
    this.editMode = false;
    this.teamName = this.args.team?.name ?? '';

    this.args.cancelAddTeam?.();
    this.args.cancelEditTeam?.();
  }

  @action
  handleUpdateField(name, value) {
    this[name] = value;
  }

  @action
  deleteTeam(company, team) {
    showModal(this, 'confirmation', {
      titleText: `Delete '${team.name}' Team?`,
      bodyText: `Are you sure you want to delete the team '${team.name}'? All members will be removed from this team and will be reverted to regular member status.`,
      actionButtonText: 'Delete',
      confirmAction: () => {
        this.deleteTask.perform(company.id, team.id);
      },
      cancelButtonText: 'Cancel',
    });
  }
}

export default CompanyProfileTeamItem;
